import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core'
import { animate, AnimationBuilder, style } from '@angular/animations'
import { TranslateService } from '@ngx-translate/core'
import { BehaviorSubject } from 'rxjs'
import { filter, switchMap, takeUntil } from 'rxjs/operators'

import { linxioAnimations } from '@shared/animations'
import { DialogService } from '@shared/dialogs/dialog.service'
import { ReportTypes } from '@shared/models/reports/types'
import { ScheduledReportFormComponent } from '@app/modules/client/config/scheduled-reports/scheduled-report-form/scheduled-report-form.component'
import { ScheduledReportApiService } from '@app/modules/client/config/scheduled-reports/scheduled-report-api.service'
import { DestroyService } from '@shared/services/destroy.service'
import { ScheduledParams, ScheduledReportModel } from '@shared/models/scheduled-report.model'
import { ScheduledReportService } from '@app/modules/client/config/scheduled-reports/scheduled-report.service'
import { PermissionsService } from '@shared/services/permissions.service'
import { StateService } from '@shared/services/state.service'
import { PermissionsSource, PermissionSourceType } from '@core/types/permissions.source'

@Component({
  selector: 'lin-sticker-schedule',
  templateUrl: './sticker-schedule.component.html',
  styleUrls: ['./sticker-schedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: linxioAnimations,
  providers: [DestroyService],
  standalone: false,
})
export class StickerScheduleComponent implements OnInit, OnDestroy {
  @Input() reportType: ReportTypes
  @Input() eventId: number
  @Input() hasXlsx: boolean

  @Output() scheduledReport = new EventEmitter<string[]>()

  @ViewChild('panel', { static: true })
  panel: ElementRef
  @ViewChild('overlay', { static: true }) overlay: ElementRef

  private removeClickListener: () => void
  scheduledReportList: ScheduledReportModel[] = []
  isLoading$ = new BehaviorSubject(false)

  @HostBinding('class.bar-closed') isBarClosed = true
  @HostBinding('style.top') stickerScheduleTop = '196px'

  constructor(
    private readonly animationBuilder: AnimationBuilder,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private dialogService: DialogService,
    private scheduledReportApiService: ScheduledReportApiService,
    private scheduledReportService: ScheduledReportService,
    private destroyService: DestroyService,
    private permissionsService: PermissionsService,
    private stateService: StateService,
  ) {}

  ngOnInit(): void {
    const handleClick = () => this.closeBar()
    this.removeClickListener = this.renderer.listen(this.overlay.nativeElement, 'click', handleClick)

    const resizeObserver = new ResizeObserver((entries) => {
      const bannerHeight = entries[0].target.clientHeight
      this.stickerScheduleTop = String(196 + bannerHeight) + 'px'
    })

    setTimeout(() => {
      const banner = document.getElementById('warning-banner')
      banner && resizeObserver.observe(document.getElementById('warning-banner'))
    })
  }

  closeBar(): void {
    this.isBarClosed = true
    this.animationBuilder
      .build([style({ transform: 'translate3d(0,0,0)' }), animate('400ms ease', style({ transform: 'translate3d(100%,0,0)' }))])
      .create(this.panel.nativeElement)
      .play()
    this.cdr.detectChanges()
  }

  openBar(): void {
    this.isBarClosed = false
    this.animationBuilder
      .build([style({ transform: 'translate3d(100%,0,0)' }), animate('400ms ease', style({ transform: 'translate3d(0,0,0)' }))])
      .create(this.panel.nativeElement)
      .play()
    this.getScheduledReportList()
    this.cdr.detectChanges()
  }

  handleAddNewSchedule(): void {
    this.updateScheduledParamsDatasource()
    this.dialogService
      .open(
        ScheduledReportFormComponent,
        <string>this.translateService.instant('CONFIGURATION.SCHEDULED_REPORTS_TITLE'),
        { type: this.reportType, hasXlsx: this.hasXlsx, ...(this.eventId && { eventId: +this.eventId }) },
        800,
      )
      .afterClosed()
      .pipe(takeUntil(this.destroyService))
      .subscribe(() => this.getScheduledReportList())
  }

  get isScheduledPermission(): boolean {
    return this.hasPermission(PermissionsSource['CONFIGURATION.SCHEDULED_REPORT_LIST'])
  }

  get isAddMode(): boolean {
    return this.hasPermission(PermissionsSource['CONFIGURATION.SCHEDULED_REPORT_LIST.ADD'])
  }

  get isEditMode(): boolean {
    return this.hasPermission(PermissionsSource['CONFIGURATION.SCHEDULED_REPORT_LIST.EDIT'])
  }

  get isDeleteMode(): boolean {
    return this.hasPermission(PermissionsSource['CONFIGURATION.SCHEDULED_REPORT_LIST.EDIT'])
  }

  get isEventLog(): boolean {
    return this.reportType === ReportTypes.EventLog
  }

  ngOnDestroy(): void {
    this.removeClickListener()
  }

  handleEdit(report: ScheduledReportModel): void {
    this.dialogService
      .open(
        ScheduledReportFormComponent,
        <string>this.translateService.instant('CONFIGURATION.SCHEDULED_REPORTS_TITLE'),
        { hasXlsx: this.hasXlsx, ...report },
        800,
      )
      .afterClosed()
      .pipe(takeUntil(this.destroyService))
      .subscribe(() => this.getScheduledReportList())
  }

  handlePreview(report: ScheduledReportModel): void {
    this.scheduledReportApiService
      .get(report.id)
      .pipe(takeUntil(this.destroyService))
      .subscribe((response) => {
        this.updateStateReport(response.params, response.type, true)
      })
  }

  handleUpdate(report: ScheduledReportModel): void {
    this.updateScheduledParamsDatasource()
    const params = { ...this.scheduledReportService.getParams(), ...(this.isEventLog && { eventId: +this.eventId }) }

    this.updateStateReport(params, report.type)

    this.dialogService
      .confirm(<string>this.translateService.instant('CONFIGURATION.SCHEDULED_REPORTS.UPDATE_MESSAGE'))
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.scheduledReportApiService.patch(report.id, { params })),
        takeUntil(this.destroyService),
      )
      .subscribe(() => this.getScheduledReportList())
  }

  handleDelete(reportId: string): void {
    this.dialogService
      .confirm(<string>this.translateService.instant('CONFIGURATION.SCHEDULED_REPORTS.DELETE_MESSAGE'))
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.scheduledReportApiService.delete(reportId)),
        takeUntil(this.destroyService),
      )
      .subscribe(() => this.getScheduledReportList())
  }

  private updateStateReport(params: ScheduledParams, reportType: ReportTypes, isPreview?: boolean): void {
    const parsingParams = this.scheduledReportService.parsingParams(params, reportType)

    this.stateService.saveFullState({
      filterValues: parsingParams.filterValues,
      sortValue: parsingParams.sortValue,
      fields: parsingParams.fields,
    })

    isPreview && this.scheduledReportService.params$.next(parsingParams)
  }

  private hasPermission(permissionType: PermissionSourceType): boolean {
    return this.permissionsService.hasPermissionSync(permissionType)
  }

  private updateScheduledParamsDatasource(): void {
    this.scheduledReport.emit()
  }

  private getScheduledReportList(): void {
    this.isLoading$.next(true)
    this.scheduledReportApiService
      .list({
        page: 0,
        limit: 1000,
        type: this.reportType,
        ...(this.isEventLog && { eventId: +this.eventId }),
      })
      .pipe(takeUntil(this.destroyService))
      .subscribe((list) => {
        this.scheduledReportList = list
        this.isLoading$.next(false)
        this.cdr.detectChanges()
      })
  }
}
